<script setup lang="ts">
import { PhCircle, PhWarningCircle, PhXCircle } from "@phosphor-icons/vue"
import { computed } from "vue"
import type { RecentEvent } from "../utils/dashboard-types"
import SessionId from "./SessionId.vue"
import { formatRelative } from "../utils/format"

interface EventTimelineItem extends RecentEvent {
  nodeCity?: string | null
  nodeCountry?: string | null
  nodeIpAddress?: string | null
  sessionCountry?: string | null
}

const props = defineProps<{
  events: EventTimelineItem[]
  now: number
}>()

const timelineRows = computed(() => props.events.map((event) => ({ event, context: contextItems(event) })))

function eventIcon(level: RecentEvent["level"]) {
  if (level === "error") {
    return PhXCircle
  }
  if (level === "warn") {
    return PhWarningCircle
  }
  return PhCircle
}

function eventLevelClass(level: RecentEvent["level"]) {
  return `dashboard-event-level--${level}`
}

type ContextItem = { key: string; label: string; sessionId?: string }

function contextItems(event: EventTimelineItem): ContextItem[] {
  const items: ContextItem[] = []

  if (event.nodeId) {
    items.push({ key: "node", label: `node ${event.nodeId}` })
  }
  if (event.nodeIpAddress) {
    items.push({ key: "ip", label: `ip ${event.nodeIpAddress}` })
  }
  if (event.nodeCountry) {
    items.push({
      key: "country",
      label: event.nodeCity ? `country ${event.nodeCountry} / ${event.nodeCity}` : `country ${event.nodeCountry}`
    })
  } else if (event.sessionCountry) {
    items.push({ key: "session-country", label: `requested country ${event.sessionCountry}` })
  }
  if (event.sessionId) {
    items.push({ key: "session", label: "session", sessionId: event.sessionId })
  }
  if (event.clientId) {
    items.push({ key: "client", label: `client ${event.clientId}` })
  }

  return items
}
</script>

<template>
  <div v-if="events.length" class="grid gap-0">
    <article
      v-for="row in timelineRows"
      :key="row.event.id"
      class="dashboard-event-row grid grid-cols-[18px_minmax(0,1fr)] gap-3 border-b border-(--dash-line) px-2 py-3 last:border-b-0"
    >
      <component
        :is="eventIcon(row.event.level)"
        :size="15"
        class="dashboard-event-icon mt-0.5"
        :class="eventLevelClass(row.event.level)"
      />
      <div class="dashboard-event-row-content min-w-0">
        <div class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
          <div class="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
            <span class="dashboard-event-badge dashboard-mono" :class="eventLevelClass(row.event.level)">
              {{ row.event.component }}
            </span>
            <span
              v-if="row.event.type"
              class="dashboard-event-badge dashboard-event-badge--outline dashboard-mono"
              :class="eventLevelClass(row.event.level)"
            >
              {{ row.event.type }}
            </span>
          </div>
          <span class="dashboard-event-time-badge dashboard-mono">
            {{ formatRelative(row.event.timestamp, now) }}
          </span>
        </div>
        <p class="mt-2 text-sm leading-5 text-(--dash-muted)">{{ row.event.message }}</p>
        <div v-if="row.context.length" class="mt-3 flex flex-wrap gap-1.5">
          <span
            v-for="item in row.context"
            :key="item.key"
            class="badge dashboard-mono inline-flex min-h-5 items-center border border-(--dash-line) px-1.5 py-0.5 text-[0.66rem] leading-none text-(--dash-muted)"
            :data-vhs-text="item.sessionId ? `SESSION ${item.sessionId}`.toUpperCase() : item.label.toUpperCase()"
          >
            <template v-if="item.sessionId">
              <span>session&nbsp;</span>
              <SessionId :value="item.sessionId" max-class="max-w-[6.5rem]" title="Session id" />
            </template>
            <template v-else>{{ item.label }}</template>
          </span>
        </div>
      </div>
    </article>
  </div>
  <p v-else class="text-sm text-(--dash-muted)">No recent events.</p>
</template>
